import type { CardPreviewDensity } from "@/components/shapes/card-previews";
import { TintBlock } from "@/components/shapes/shared/card-visual-parts";
import { cn } from "@/lib/cn";
import { domainFromUrl, type PreviewLink } from "./link-preview-links";

/** The site's name for its cover: the site name, else the domain's name. */
function siteGlyph(siteName: string | undefined, domain: string): string {
  if (siteName) return siteName;
  const parts = domain.split(".");
  return parts.length > 1 ? parts[parts.length - 2]! : domain;
}

/**
 * A link's cover (mockup `.cover`), the card and the top of its opened
 * window: the site's own tint, a favicon swatch and the domain at the top,
 * the site's name large at the foot.
 */
export function LinkCover({
  link,
  density,
}: {
  link: PreviewLink;
  density: CardPreviewDensity;
}) {
  const domain = domainFromUrl(link.url);
  return (
    <TintBlock
      seed={domain}
      className={cn(
        "flex shrink-0 flex-col justify-between overflow-hidden p-[1.4em]",
        density === "compact" ? "h-full" : "aspect-[1.15]",
      )}
      data-testid="link-card-cover"
    >
      <span className="flex min-w-0 items-center gap-1.5 text-[0.8em] font-medium opacity-85">
        <i
          aria-hidden="true"
          className="h-3 w-3 shrink-0 rounded-[3px] bg-current opacity-80"
        />
        <span className="truncate">{domain}</span>
      </span>
      <span className="line-clamp-3 font-display text-[1.9em] font-medium leading-[1.02] tracking-[-0.02em]">
        {siteGlyph(link.siteName, domain)}
      </span>
    </TintBlock>
  );
}
